import { DEFAULT_SECTION_ORDER } from '@nexus/core';
import {
  ErrorBoundary,
  ErrorState,
  PageSection,
  Skeleton,
  type IconName,
  type SectionTab,
} from '@nexus/ui';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { lazy, Suspense, useMemo, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import type { SessionInfo } from '../api/types';
import { useModules } from '../modules/context';
import type { RecordSectionProps, WebModule, WebRecordSection } from '../modules/define';
import { canIn, useSession } from '../session/session';

interface VisibleSection {
  key: string;
  /** i18n key of the heading. */
  title: string;
  order: number;
  /** Icon of the section, or of the module it comes from. */
  icon: IconName;
  Content: ComponentType<RecordSectionProps>;
}

// One lazy component per section, so React keeps it loaded between renders.
const lazySections = new WeakMap<WebRecordSection, ComponentType<RecordSectionProps>>();

function contentOf(section: WebRecordSection): ComponentType<RecordSectionProps> {
  let component = lazySections.get(section);
  if (!component) {
    component = lazy(section.load);
    lazySections.set(section, component);
  }
  return component;
}

/**
 * Sections of active modules (and active parents) for a target the person has the permission
 * for, by `order`, then by key. The API behind a section checks again; this only hides it.
 */
function sectionsFor(
  target: string,
  modules: readonly WebModule[],
  session: SessionInfo,
): VisibleSection[] {
  const active = new Set(session.modules);
  return modules
    .filter(
      (m) => active.has(m.manifest.id) && (!m.manifest.parent || active.has(m.manifest.parent)),
    )
    .flatMap((module) =>
      (module.manifest.recordSections ?? [])
        .filter((section) => section.target === target && canIn(session, section.permission))
        .flatMap((section) => {
          const component = module.recordSections?.find((c) => c.key === section.key);
          if (!component) return [];
          return [
            {
              key: section.key,
              title: section.title,
              order: section.order ?? DEFAULT_SECTION_ORDER,
              icon: section.icon ?? module.manifest.icon,
              Content: contentOf(component),
            },
          ];
        }),
    )
    .sort((a, b) => a.order - b.order || a.key.localeCompare(b.key));
}

export interface RecordSectionsProps {
  /** Target the page offers in its manifest under `sectionTargets`, e.g. `crm.organization`. */
  target: string;
  /** ID of the record the page shows. */
  recordId: string;
}

/**
 * Sections other modules show on a detail page, e.g. the projects of a customer on the page of a
 * CRM organisation. Each section loads and fails on its own, so a broken module never takes the
 * page down (docs/architecture/modules.md, "Abschnitte auf Detailseiten").
 */
export function RecordSections({ target, recordId }: RecordSectionsProps) {
  const { t } = useTranslation();
  const sections = useVisibleSections(target);

  return sections.map(({ key, title, Content }) => (
    <PageSection key={key} title={t(title)}>
      <SectionBody Content={Content} recordId={recordId} />
    </PageSection>
  ));
}

/**
 * The same sections as tabs for `SectionTabs`, one per section, with the icon of its module.
 * The page puts them between its own tabs; the value of a tab is the key of the section.
 */
export function useRecordSectionTabs(target: string, recordId: string): SectionTab[] {
  const { t } = useTranslation();
  const sections = useVisibleSections(target);
  return sections.map(({ key, title, icon, Content }) => ({
    value: key,
    label: t(title),
    icon,
    content: (
      <PageSection title={t(title)}>
        <SectionBody Content={Content} recordId={recordId} />
      </PageSection>
    ),
  }));
}

function useVisibleSections(target: string): VisibleSection[] {
  const session = useSession();
  const modules = useModules();
  return useMemo(() => sectionsFor(target, modules, session), [target, modules, session]);
}

/** Loads and fails on its own, so a broken module never takes the page down. */
function SectionBody({
  Content,
  recordId,
}: {
  Content: ComponentType<RecordSectionProps>;
  recordId: string;
}) {
  const { t } = useTranslation();
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        // Keyed by record: a failure on one record does not stick when the page shows another.
        <ErrorBoundary
          key={recordId}
          onReset={reset}
          fallback={({ reset: retry }) => (
            <ErrorState compact title={t('recordSections.failed')} onRetry={retry} />
          )}
        >
          <Suspense fallback={<Skeleton className="h-24 w-full" />}>
            <Content recordId={recordId} />
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

/**
 * Every record section in a manifest has exactly one component in the web module, and every
 * component a declared section. Runs when the router is built and in the tests of each module.
 */
export function checkRecordSections(modules: readonly WebModule[]): void {
  const problems: string[] = [];
  for (const module of modules) {
    const { id } = module.manifest;
    const declared = new Set((module.manifest.recordSections ?? []).map((s) => s.key));
    const components = (module.recordSections ?? []).map((s) => s.key);
    for (const key of declared) {
      const count = components.filter((c) => c === key).length;
      if (count === 0) problems.push(`${id}: record section "${key}" has no component`);
      if (count > 1) problems.push(`${id}: record section "${key}" has ${count} components`);
    }
    for (const key of components) {
      if (!declared.has(key)) {
        problems.push(`${id}: record section component "${key}" is not declared in the manifest`);
      }
    }
  }
  if (problems.length > 0) throw new Error(problems.join('\n'));
}
